<script setup>
import { RouterLink, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores'
import { computed, onMounted } from 'vue'

import DashboardIcon from '@/components/icons/DashboardIcon.vue'
import ContractsIcon from '@/components/icons/ContractsIcon.vue'
import QuotesIcon from '@/components/icons/QuotesIcon.vue'
import InvoicesIcon from '@/components/icons/InvoicesIcon.vue'
import ReportsIcon from '@/components/icons/ReportsIcon.vue'
import ToolsIcon from '@/components/icons/ToolsIcon.vue'
import LogoutIcon from '@/components/icons/LogoutIcon.vue'

const authStore = useAuthStore()

const route = useRoute()

const allNavItems = [
  {
    title: 'Dashboard',
    icon: DashboardIcon,
    to: '/',
    children: [],
    roles: ['admin']
  },
  {
    title: 'Insurance',
    icon: ContractsIcon,
    to: '#',
    children: [
      {
        title: 'Policies',
        to: '/insurance/contracts',
        roles: ['admin']
      },
      {
        title: 'Applications',
        to: '/insurance/applications',
        roles: ['admin', 'broker']
      }
    ],
    roles: ['admin', 'broker']
  },
  {
    title: 'Yacht Management',
    icon: QuotesIcon,
    to: '#',
    children: [
      {
        title: 'Contracts',
        to: '/yacht-management/contracts'
      },
      {
        title: 'Applications',
        to: '/yacht-management/applications'
      }
    ],
    roles: ['admin']
  },
  {
    title: 'Invoices',
    icon: InvoicesIcon,
    to: '/invoices',
    children: [],
    roles: ['admin']
  },
  {
    title: 'Charters',
    icon: ReportsIcon,
    to: '/charters',
    children: [],
    roles: ['admin']
  },
  {
    title: 'Tools',
    icon: ToolsIcon,
    to: '#',
    children: [
      {
        title: 'YM services',
        to: '/tools/ym-services'
      },
      {
        title: 'YM rating',
        to: '/tools/ym-rating'
      },
      {
        title: 'Regional settings',
        to: '/tools/regional-settings'
      },
      {
        title: 'Insurance notifications',
        to: '/tools/insurance-notifications'
      },
      {
        title: 'Quotes',
        to: '/quotes'
      }
    ],
    roles: ['admin']
  }
]

const navItems = computed(() => {
  if (!authStore.userPermissions || authStore.userPermissions.length === 0) {
    return []
  }

  const userRoles = []
  if (authStore.userPermissions.includes('role:admin')) {
    userRoles.push('admin')
  }
  if (authStore.userPermissions.includes('role:broker')) {
    userRoles.push('broker')
  }

  if (userRoles.includes('admin')) {
    return allNavItems
  }

  if (userRoles.includes('broker')) {
    return allNavItems
      .filter((item) => item.roles.includes('broker'))
      .map((item) => ({
        ...item,
        children: (item.children || []).filter(
          (child) => !child.roles || child.roles.includes('broker')
        )
      }))
      .filter((item) => item.children.length > 0 || !item.children.length)
  }

  return []
})

onMounted(async () => {
  if (authStore.userToken && authStore.userPermissions.length === 0) {
    try {
      await authStore.fetchUserPermissions()
    } catch (error) {
      console.error('Failed to fetch permissions in MainNav:', error)
    }
  }
})

const isActive = (path) => {
  return route.path === path
}
</script>

<template>
  <nav class="hs-accordion-group p-6 w-full flex flex-col flex-wrap" data-hs-accordion-always-open>
    <ul class="space-y-1.5">
      <li
        v-for="item in navItems"
        :class="item.children.length ? 'hs-accordion' : ''"
        :key="item.to"
      >
        <button
          v-if="item.children.length"
          type="button"
          class="hs-accordion-toggle w-full text-start flex items-center gap-x-3.5 py-2 px-2.5 hs-accordion-active:text-white hs-accordion-active:hover:bg-transparent text-sm text-gray-400 rounded-lg hover:bg-gray-800 hover:text-white focus:outline-none focus:ring-1 focus:ring-gray-600"
        >
          <component :is="item.icon" />
          {{ item.title }}

          <svg
            class="hs-accordion-active:block ms-auto hidden w-4 h-4"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m18 15-6-6-6 6" />
          </svg>

          <svg
            class="hs-accordion-active:hidden ms-auto block w-4 h-4"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>

        <RouterLink
          v-if="!item.children.length"
          :to="item.to"
          :class="[
            'flex items-center gap-x-3.5 py-2 px-2.5 text-sm rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-600',
            isActive(item.to)
              ? 'bg-gray-700 text-white'
              : 'text-gray-400 hover:bg-gray-800 hover:text-white-300'
          ]"
        >
          <component :is="item.icon" />
          {{ item.title }}
        </RouterLink>

        <div
          id="projects-accordion-child"
          class="hs-accordion-content w-full overflow-hidden transition-[height] duration-300 hidden"
        >
          <ul v-if="item.children && item.children.length" class="pt-2 ps-2">
            <li v-for="child in item.children" :key="child.to" class="p-px">
              <RouterLink
                :to="child.to"
                :class="[
                  'flex items-center gap-x-3.5 py-2 px-2.5 text-sm rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-600',
                  isActive(child.to)
                    ? 'bg-gray-700 text-white'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white-300'
                ]"
              >
                {{ child.title }}
              </RouterLink>
            </li>
          </ul>
        </div>
      </li>
    </ul>
  </nav>

  <div class="px-6 pt-6 mt-auto">
    <button
      @click="authStore.logout"
      class="flex items-center w-full gap-x-3.5 py-2 px-2.5 text-sm rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-600 text-gray-400 hover:bg-gray-800 hover:text-white-300"
    >
      <component :is="LogoutIcon" />
      Logout
    </button>
  </div>
</template>
